import { useAppDisplayModeContext } from '@archipro-design/aria';
import { VariantStockTypeEnum } from 'generated/graphql';
import React from 'react';
import { pluralize } from '~/utils/stringHelper';
import { useVariantSelectionState } from '../context/VariantSelectionContext';

const QUANTITY_LIMIT = 9999;

interface ReturnType {
    showAsInStock: boolean;
    canAddToCart: boolean;
    stockDescription: string;
    quantityLimit: number;
    hasLeadTime: boolean;
}

interface VariantStockProps {
    hasClickAndCollect?: boolean;
    hasShippingType?: boolean;
    mustHaveSize?: boolean;
}

export const useVariantStock = (props?: VariantStockProps): ReturnType => {
    const {
        selectedVariant: variant,
        quantity,
        setQuantity,
    } = useVariantSelectionState();

    const {
        hasClickAndCollect = true,
        hasShippingType = true,
        mustHaveSize = false, //ignore the mustHaveSize by default
    } = props || {};

    const result: ReturnType = {
        showAsInStock: false,
        canAddToCart: false,
        stockDescription: 'Out of stock',
        quantityLimit: 0,
        hasLeadTime: false,
    };

    const { mobile } = useAppDisplayModeContext();

    const backOrderEnabled = variant?.AllowsBackOrder && variant?.LeadTimeUnit;

    const availableStock =
        variant && variant.AvailableStock > 0 ? variant.AvailableStock : 0;

    React.useEffect(() => {
        if (variant) {
            const limit = variant.AvailableStock;
            if (quantity > limit && !backOrderEnabled && limit > 0) {
                setQuantity(limit);
            }
        }
    }, [variant, quantity, setQuantity, backOrderEnabled]);
    if (!variant) {
        return result;
    }

    result.quantityLimit = availableStock;

    const leadTimeText =
        variant.LeadTime > 0 && variant.LeadTimeUnit
            ? ` (${pluralize(
                  variant.LeadTime,
                  variant.LeadTimeUnit,
              ).toLowerCase()})`
            : '';

    const canPurchase = variant.CanPurchase;

    switch (variant.StockType) {
        case VariantStockTypeEnum.Indent:
            result.stockDescription =
                (mobile ? 'Indent order' : 'Coming from overseas') +
                leadTimeText;
            result.showAsInStock = true;
            result.canAddToCart = variant.CanAddToCart;
            result.hasLeadTime = !!leadTimeText;
            break;

        case VariantStockTypeEnum.MadeToOrder:
            result.showAsInStock = true;
            result.canAddToCart = variant.CanAddToCart;
            result.stockDescription = 'Made to order' + leadTimeText;
            result.hasLeadTime = !!leadTimeText;
            break;
        case VariantStockTypeEnum.InStock:
        case null:
            if (mustHaveSize && !hasClickAndCollect && !variant.Size) {
                result.stockDescription = 'No size available';
                result.canAddToCart = false;
                result.quantityLimit = QUANTITY_LIMIT;
                result.hasLeadTime = !!leadTimeText;
            } else if (!hasShippingType) {
                result.stockDescription = 'No shipping type available';
                result.canAddToCart = false;
                result.quantityLimit = QUANTITY_LIMIT;
                result.hasLeadTime = !!leadTimeText;
            } else if (availableStock > 0 && quantity <= availableStock) {
                result.stockDescription = canPurchase
                    ? 'In Stock'
                    : 'Out of stock';
                result.showAsInStock = true;
                result.canAddToCart = variant.CanAddToCart;
                result.quantityLimit = variant.AvailableStock;
            } else if (backOrderEnabled && variant.AvailableStock <= 0) {
                result.stockDescription = 'Backorder available' + leadTimeText;
                result.canAddToCart = variant.CanAddToCart;
                result.quantityLimit = QUANTITY_LIMIT;
                result.hasLeadTime = !!leadTimeText;
            } else {
                result.canAddToCart = false;
                result.quantityLimit = 0;
            }
            break;
    }
    result.quantityLimit = Math.min(result.quantityLimit, QUANTITY_LIMIT);

    return result;
};
